import { ReactNode, useCallback, useState } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Text } from 'react-aria-components';
import Button from 'src/components/ui/Button';
import PageTitle from 'src/components/creator/PageTitle';
import PageLoader from 'src/components/ui/PageLoader';
import ChatHistory from 'src/components/paul/ChatHistory';
import ChatBoxForm from 'src/components/paul/ChatBoxForm';
import TagButton from 'src/components/ui/TagButton';
import PenIcon from 'src/assets/icons/content-pen-write-1.svg';
import HatIcon from 'src/assets/icons/graduation-hat.svg';
import HandIcon from 'src/assets/icons/hand-stop.svg';
import HouseIcon from 'src/assets/icons/house-chimney-2.svg';
import SchoolIcon from 'src/assets/icons/school-building.svg';

import useUser from 'src/hooks/useUser';
import usePaulSemanticSearch from 'src/hooks/usePaulSemanticSearch';

import CheckIcon from 'src/assets/icons/shield-check.svg';

import type { ClientInformation } from 'src/types/client';

const PaulTestPage = () => {
  const { user, loading: userLoading } = useUser();
  const [orderCreated, setOrderCreated] = useState(false);
  const [restartCount, setRestartCount] = useState(0);

  if (userLoading) return <PageLoader />;
  if (!user.hasLadLibrarySearch) return <Navigate to="/" />;

  return (
    <>
      <PageTitle
        title="AI Assisted Search"
        actionSlot={
          <Button
            onPress={() => setRestartCount((p) => p + 1)}
            variant="success"
          >
            Start Over
          </Button>
        }
      />
      <div className="bg-surface-alt rounded-xl shadow-3 mt-4 p-6 flex justify-center items-center flex-1 min-h-0">
        {orderCreated ? (
          <OrderCreatedScreen onSearch={() => setOrderCreated(false)} />
        ) : (
          <Content
            key={restartCount}
            user={user}
            onCreateOrder={() => setOrderCreated(true)}
          />
        )}
      </div>
    </>
  );
};

const Content = ({
  user,
  onCreateOrder,
}: {
  user: ClientInformation;
  onCreateOrder: () => void;
}) => {
  const { askPaul, loading, results } = usePaulSemanticSearch();
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const hasMaterials = selectedMaterials.length > 0;

  const onMaterialToggle = useCallback(
    (material: { materialId: string; isSelected: boolean }) => {
      setSelectedMaterials((prevState) => {
        if (material.isSelected) return [...prevState, material.materialId];

        return prevState.filter(
          (itemId) => Number(itemId) !== Number(material.materialId)
        );
      });
    },
    []
  );

  if (results.length === 0) {
    return (
      <EmptyState
        username={user?.firstName ?? user?.lastName ?? user?.email ?? 'User'}
      >
        <ChatBoxForm onSubmit={(prompt) => askPaul(prompt)} loading={loading} />
        <div className="flex gap-2 flex-wrap mt-4 px-4 mb-1.5">
          <TagButton variant="ai" Icon={PenIcon} title="OER Materials" />
          <TagButton variant="ai" Icon={HatIcon} title="Out-of-Print Library" />
          <TagButton variant="ai" Icon={HandIcon} title="LAD Library" />
          <TagButton variant="ai" Icon={HouseIcon} title="Non Copyrighted" />
          <TagButton variant="ai" Icon={SchoolIcon} title="Business Cases" />
        </div>
      </EmptyState>
    );
  }

  return (
    <ChatHistory
      user={user}
      results={results}
      onMaterialToggle={onMaterialToggle}
      selectedMaterials={selectedMaterials}
    >
      <div className="flex items-start justify-center gap-4 px-7">
        <div
          className={`min-h-16 ${hasMaterials ? 'flex-1' : 'w-full max-w-[30rem]'}`}
        >
          <ChatBoxForm
            small
            onSubmit={(prompt) => askPaul(prompt)}
            loading={loading}
          />
        </div>
        {hasMaterials && (
          <Button
            className="animate-in slide-in-from-right duration-200 ease-out"
            onPress={onCreateOrder}
            variant="success"
          >
            Create Order
          </Button>
        )}
      </div>
    </ChatHistory>
  );
};

const OrderCreatedScreen = ({ onSearch }: { onSearch: () => void }) => (
  <div className="text-center max-w-100 px-4">
    <CheckIcon className="inline-block w-12 h-12" />
    <Text
      elementType="strong"
      className="block mt-2 text-surface-alt-90 text-2xl font-bold"
    >
      New Order Created!
    </Text>
    <Text
      elementType="p"
      className="mt-2 mb-8 text-surface-alt-90 text-sm font-medium"
    >
      Thank you for starting a new order with LAD. From here you can continue
      your search for materials, review/edit your order, save it for later, or
      begin the check-out process by sending a notification to our LAD team to
      begin a production review.
    </Text>
    <Button onPress={onSearch} variant="secondary" className="justify-center">
      Search again
    </Button>
  </div>
);

const EmptyState = ({
  username,
  children,
}: {
  username: string;
  children: ReactNode;
}) => (
  <div className="text-center text-surface-alt-90 w-full max-w-[44rem] px-4 max-h-full overflow-auto">
    <Text elementType="strong" className="text-2xl font-bold">
      Hello, {username}
    </Text>
    <Text elementType="p" className="text-sm font-medium mt-2 mb-8">
      I'm here to help you find materials <br /> for your new Course Pack.
    </Text>

    {children}
  </div>
);

export const Route = createFileRoute('/_authenticated/_creator/paul')({
  component: PaulTestPage,
});
